import { jsPDF } from 'jspdf';

export interface ReportCardRow {
  categoryLabel: string;
  roleLabel?: string;
  periodLabel: string;
  levelLabel: string;
  gradeWithCategory: string;
  pts: string;
}

export interface ReportCardSport {
  sport: string;
  isPrimarySport: boolean;
  isSecondarySport: boolean;
  self: ReportCardRow[];
  coach: ReportCardRow[];
  encycgames: ReportCardRow[];
}

interface GenerateDashboardReportCardPdfArgs {
  playerName: string;
  academyName?: string;
  generatedAt?: string;
  sports: ReportCardSport[];
}

export const generateDashboardReportCardPDF = async ({
  playerName,
  academyName,
  generatedAt,
  sports,
}: GenerateDashboardReportCardPdfArgs): Promise<void> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - margin * 2;
  const footerHeight = 14;

  const addFooter = () => {
    const footerY = pageHeight - footerHeight;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(margin, footerY, pageWidth - margin, footerY);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Encycdata | EncycGames', margin, footerY + 5);
    doc.text(
      `Page ${doc.getCurrentPageInfo().pageNumber} of ${doc.getNumberOfPages()}`,
      pageWidth / 2,
      footerY + 5,
      { align: 'center' }
    );
    doc.text(
      generatedAt || new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
      pageWidth - margin,
      footerY + 5,
      { align: 'right' }
    );
  };

  let y = 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Encycdata', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('EncycGames', margin, y + 5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Player Report Card', pageWidth / 2, y + 2, { align: 'center' });
  y += 14;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Player: ${playerName || 'N/A'}`, margin, y);
  doc.text(`Academy: ${academyName || 'N/A'}`, pageWidth / 2, y);
  y += 7;

  const columnGap = 3;
  const sportCardPadding = 2.5;
  const colWidth = (contentWidth - columnGap * 2 - sportCardPadding * 2) / 3;
  const lineHeight = 3.8;

  const sectionTitle = (x: number, topY: number, title: string) => {
    doc.setFillColor(232, 242, 255);
    doc.rect(x, topY, colWidth, 5, 'F');
    doc.setDrawColor(180, 200, 230);
    doc.rect(x, topY, colWidth, 5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text(title, x + colWidth / 2, topY + 3.6, { align: 'center' });
  };

  const drawRows = (x: number, topY: number, rows: ReportCardRow[]) => {
    let rowY = topY;
    if (rows.length === 0) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7.5);
      doc.setTextColor(110, 110, 110);
      doc.text('No data', x + 1.5, rowY + 4);
      doc.setTextColor(0, 0, 0);
      return rowY + 7;
    }

    rows.forEach((r, idx) => {
      if (idx > 0) {
        doc.setDrawColor(230, 230, 230);
        doc.line(x, rowY, x + colWidth, rowY);
      }
      rowY += 1.6;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.3);
      doc.text(r.categoryLabel || 'N/A', x + 1, rowY + lineHeight);
      rowY += lineHeight + 0.2;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(`Role: ${r.roleLabel || 'General'}`, x + 1, rowY + lineHeight);
      rowY += lineHeight;
      doc.text(r.periodLabel || '–', x + 1, rowY + lineHeight);
      rowY += lineHeight;
      doc.text(r.levelLabel || '–', x + 1, rowY + lineHeight);
      rowY += lineHeight;
      doc.setFont('helvetica', 'bold');
      doc.text(r.gradeWithCategory || '–', x + 1, rowY + lineHeight);
      rowY += lineHeight;
      doc.text(r.pts || '–', x + 1, rowY + lineHeight);
      rowY += lineHeight + 1.2;
    });
    return rowY;
  };

  for (const sportCard of sports) {
    const expectedHeight = 62;
    if (y + expectedHeight > pageHeight - footerHeight - 5) {
      addFooter();
      doc.addPage();
      y = 14;
    }

    const cardX = margin;
    const cardY = y;
    const cardW = contentWidth;
    const cardH = expectedHeight;

    doc.setDrawColor(195, 210, 235);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(cardX, cardY, cardW, cardH, 1.5, 1.5, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    const sportHeader = `${sportCard.sport}${sportCard.isPrimarySport ? '  |  Primary' : sportCard.isSecondarySport ? '  |  Secondary' : ''}`;
    doc.text(sportHeader, cardX + 2.5, cardY + 5.5);

    const columnsTop = cardY + 8;
    const col1X = cardX + sportCardPadding;
    const col2X = col1X + colWidth + columnGap;
    const col3X = col2X + colWidth + columnGap;

    sectionTitle(col1X, columnsTop, 'Self');
    sectionTitle(col2X, columnsTop, 'Coach');
    sectionTitle(col3X, columnsTop, 'EncycGames');

    const bodyTop = columnsTop + 5.6;
    drawRows(col1X, bodyTop, sportCard.self);
    drawRows(col2X, bodyTop, sportCard.coach);
    drawRows(col3X, bodyTop, sportCard.encycgames);

    y += cardH + 4;
  }

  addFooter();
  const safePlayerName = (playerName || 'Player').replace(/\s+/g, '_');
  const fileDate = new Date().toISOString().split('T')[0];
  doc.save(`EncycGames_Report_Card_${safePlayerName}_${fileDate}.pdf`);
};

